import React, { useEffect, useState, useCallback } from "react";
import { useLocation, Link } from "react-router-dom";
import { toast } from "sonner";
import { Header } from "../components/Header";
import { PositionTracker } from "../components/PositionTracker";
import { OptionChainTable } from "../components/OptionChainTable";
import { RollRecommendation } from "../components/RollRecommendation";
import { PayoffChart } from "../components/PayoffChart";
import { MiniHeatmap } from "../components/MiniHeatmap";
import { ExpiringBanner } from "../components/ExpiringBanner";
import { STRATEGIES, strategyFromPath } from "../lib/strategies";
import {
  getChain,
  getQuote,
  listPositions,
  createPosition,
  deletePosition,
  getHeatmap,
  analyzeRoll,
  executeRoll,
  getWatchlist,
  addToWatchlist,
  removeFromWatchlist,
} from "../lib/api";

export default function Dashboard() {
  const location = useLocation();
  const strategy = strategyFromPath(location.pathname);
  const [tickers, setTickers] = useState([]);
  const [chains, setChains] = useState({});
  const [positions, setPositions] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [heatmap, setHeatmap] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expFilter, setExpFilter] = useState("ALL");

  // Bootstrap: watchlist + first chain + positions
  useEffect(() => {
    (async () => {
      try {
        const wl = await getWatchlist();
        const symbols = wl.symbols || [];
        if (symbols.length) {
          const first = symbols[0];
          try {
            const ch = await getChain(first);
            setTickers(
              symbols.map((s) => ({
                symbol: s,
                name: s === first ? ch.name : s,
                price: s === first ? ch.underlying_price : 0,
              }))
            );
            setChains((p) => ({ ...p, [first]: ch }));
          } catch {
            setTickers(symbols.map((s) => ({ symbol: s, name: s, price: 0 })));
          }
        }
        const pos = await listPositions(strategy.key);
        setPositions(pos);
        if (pos.length) setSelectedId(pos[0].id);

        // Populate prices for all watchlist + position symbols (lightweight quote endpoint)
        const allSymbols = Array.from(
          new Set([...(symbols || []), ...pos.map((p) => p.symbol)])
        );
        const quotes = await Promise.allSettled(allSymbols.map((s) => getQuote(s)));
        setTickers((prev) => {
          const map = new Map(prev.map((t) => [t.symbol, t]));
          allSymbols.forEach((sym, i) => {
            const q = quotes[i];
            if (q.status === "fulfilled") {
              map.set(sym, {
                symbol: sym,
                name: q.value.name,
                price: q.value.price,
                source: q.value.source,
              });
            } else if (!map.has(sym)) {
              map.set(sym, { symbol: sym, name: sym, price: 0 });
            }
          });
          return Array.from(map.values());
        });
      } catch (e) {
        toast.error("Failed to load workspace");
      }
    })();
  }, []);

  // Whenever the selected position changes → fetch heatmap
  useEffect(() => {
    if (!selectedId) {
      setHeatmap(null);
      setAnalysis(null);
      return;
    }
    setLoading(true);
    setAnalysis(null);
    getHeatmap(selectedId)
      .then((h) => setHeatmap(h))
      .catch((err) => {
        const detail = err?.response?.data?.detail || "Heatmap failed";
        toast.error(detail);
      })
      .finally(() => setLoading(false));
  }, [selectedId]);

  // Refetch when the strategy tab changes (URL navigation)
  useEffect(() => {
    setSelectedId(null);
    setHeatmap(null);
    setAnalysis(null);
    listPositions(strategy.key)
      .then((pos) => {
        setPositions(pos);
        if (pos.length) setSelectedId(pos[0].id);
      })
      .catch(() => setPositions([]));
  }, [strategy.key]);

  const onPickTicker = useCallback(
    async (symbol) => {
      if (!chains[symbol]) {
        try {
          const ch = await getChain(symbol);
          setChains((p) => ({ ...p, [symbol]: ch }));
        } catch {
          toast.error("Failed to load chain");
        }
      }
    },
    [chains]
  );

  const onAddCustomTicker = useCallback(async (symbol) => {
    const sym = symbol.toUpperCase().trim();
    if (!sym) return false;
    try {
      const ch = await getChain(sym);
      setChains((p) => ({ ...p, [sym]: ch }));
      setTickers((prev) => {
        if (prev.some((t) => t.symbol === sym)) {
          return prev.map((t) =>
            t.symbol === sym ? { ...t, price: ch.underlying_price, name: ch.name, source: ch.source } : t
          );
        }
        return [
          ...prev,
          { symbol: sym, name: ch.name || sym, price: ch.underlying_price, source: ch.source },
        ];
      });
      // Persist to user's watchlist
      try { await addToWatchlist(sym); } catch {}
      toast.success(`${sym} loaded · $${ch.underlying_price.toFixed(2)}`);
      return true;
    } catch (e) {
      toast.error(`Failed to load ${sym} from Tradier`);
      return false;
    }
  }, []);

  const onAddPosition = async (body) => {
    try {
      const p = await createPosition({ ...body, strategy: strategy.key });
      setPositions((prev) => [p, ...prev]);
      setSelectedId(p.id);
      toast.success(`Position added: ${p.symbol} $${p.strike}C`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add position");
    }
  };

  const onDeletePosition = async (id) => {
    try {
      await deletePosition(id);
      setPositions((prev) => prev.filter((p) => p.id !== id));
      if (selectedId === id) setSelectedId(null);
      toast.success("Position deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  const onSelectCandidate = async (cand) => {
    if (!selectedId) return;
    try {
      const a = await analyzeRoll({
        position_id: selectedId,
        new_strike: cand.strike,
        new_expiration: cand.expiration,
      });
      setAnalysis(a);
    } catch {
      toast.error("Analyze failed");
    }
  };

  const onExecuteRoll = async ({ keep_old }) => {
    if (!analysis || !selectedId) return;
    try {
      const r = await executeRoll(selectedId, {
        new_strike: analysis.new_option.strike,
        new_expiration: analysis.new_option.expiration,
        keep_old,
      });
      const fresh = await listPositions(strategy.key);
      setPositions(fresh);
      const newId = r.new_position?.id;
      if (newId) setSelectedId(newId);
      else if (!keep_old) setSelectedId(fresh[0]?.id ?? null);
      setAnalysis(null);
      toast.success(keep_old ? "Roll added (old position kept)" : "Position rolled");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Roll failed");
    }
  };

  const selectedPosition = positions.find((p) => p.id === selectedId);
  const currentCandidate = analysis?.new_option;

  return (
    <div className="App flex flex-col h-screen">
      <Header />
      <ExpiringBanner />
      <main className="flex-1 min-h-0 flex flex-col max-w-[1600px] w-full mx-auto px-4 md:px-6 pt-3 pb-3 gap-3">
        {/* Strategy tabs */}
        <nav className="flex items-stretch border border-[#27272A] bg-[#0A0A0A]" data-testid="strategy-tabs">
          {STRATEGIES.map((s) => {
            const active = s.key === strategy.key;
            return (
              <Link
                key={s.key}
                to={s.route}
                data-testid={`strategy-tab-${s.key}`}
                className={`flex-1 flex flex-col items-center justify-center px-3 py-2 border-r border-[#27272A] last:border-r-0 transition-colors ${
                  active ? "bg-[#141414]" : "hover:bg-[#141414]"
                }`}
                style={active ? { borderBottom: `2px solid ${s.accent}` } : undefined}
              >
                <div className="flex items-baseline gap-2">
                  <span className="text-[10px] font-mono font-bold" style={{ color: s.accent }}>{s.short}</span>
                  <span className={`text-xs font-semibold tracking-tight ${active ? "text-white" : "text-[#A1A1AA]"}`}>{s.label}</span>
                </div>
                <span className="text-[9px] text-[#71717A] mt-0.5 leading-tight text-center">{s.tagline}</span>
              </Link>
            );
          })}
        </nav>
        {/* Compact stats strip */}
        <section
          className="flex flex-wrap items-center gap-x-5 gap-y-1 border border-[#27272A] bg-[#0A0A0A] px-3 py-2 text-xs"
          data-testid="stats-strip"
        >
          <StripInline label="Watchlist" value={tickers.length} />
          <span className="text-[#27272A]">·</span>
          <StripInline label="Open" value={positions.length} />
          <span className="text-[#27272A]">·</span>
          <StripInline label="Candidates" value={heatmap?.candidates?.length || 0} />
          <span className="text-[#27272A]">·</span>
          <StripInline
            label="Best"
            value={heatmap?.candidates?.[0]?.score ?? "—"}
            tone={heatmap?.candidates?.[0]?.category?.startsWith("credit_strong") ? "green" : "neutral"}
          />
        </section>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 md:gap-6 flex-1 min-h-0">
          <aside className="lg:col-span-3 lg:overflow-y-auto lg:min-h-0" data-testid="left-col">
            <PositionTracker
              positions={positions}
              tickers={tickers}
              chains={chains}
              selectedId={selectedId}
              onSelect={setSelectedId}
              onAdd={onAddPosition}
              onDelete={onDeletePosition}
              onPickTicker={onPickTicker}
              onAddCustomTicker={onAddCustomTicker}
              strategy={strategy}
            />
          </aside>

          <section className="lg:col-span-6 space-y-4 md:space-y-6 lg:overflow-y-auto lg:min-h-0" data-testid="center-col">
            <OptionChainTable
              heatmap={heatmap}
              onSelectCandidate={onSelectCandidate}
              selectedCandidate={currentCandidate}
              expFilter={expFilter}
              setExpFilter={setExpFilter}
            />
            <PayoffChart
              position={selectedPosition}
              underlying={heatmap?.underlying_price}
              currentOption={heatmap?.current_option}
              newOption={currentCandidate}
              netCredit={analysis?.metrics?.net_credit}
            />
          </section>

          <aside className="lg:col-span-3 flex flex-col gap-4 md:gap-6 lg:min-h-0" data-testid="right-col">
            <div className="flex-shrink-0">
              <RollRecommendation
                analysis={analysis}
                onExecuteRoll={onExecuteRoll}
                onClose={() => setAnalysis(null)}
              />
            </div>
            {heatmap?.candidates?.length > 0 && (
              <div className="flex-1 min-h-0 flex">
                <MiniHeatmap
                  candidates={heatmap.candidates}
                  underlying={heatmap.underlying_price}
                  position={heatmap.position}
                  selectedCandidate={currentCandidate}
                  onPick={onSelectCandidate}
                  expFilter={expFilter}
                  setExpFilter={setExpFilter}
                />
              </div>
            )}
          </aside>
        </div>

        {loading && (
          <div className="fixed bottom-4 right-4 text-[11px] font-mono text-[#A1A1AA] bg-[#121212] border border-[#27272A] px-3 py-1.5">
            LOADING…
          </div>
        )}
      </main>
    </div>
  );
}

const StripInline = ({ label, value, tone = "neutral" }) => (
  <div className="flex items-baseline gap-1.5">
    <span className="label-cap">{label}</span>
    <span className={`font-mono text-sm ${tone === "green" ? "text-[#00E676]" : "text-white"}`}>
      {value}
    </span>
  </div>
);
